import { EthenaCooldownAbi } from '../abis/ethena'
import type { LstWithdrawalRequest } from '../types'
import { computeStatus, toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Ethena-style cooldown reader (sUSDe, and any savings vault exposing
 * the same `cooldowns(address)` getter).
 *
 * One call: `cooldowns(user) → (cooldownEnd, underlyingAmount)`. A
 * non-zero `underlyingAmount` means the user has assets escrowed in the
 * cooldown; `cooldownEnd` is the unlock time. `status` is derived from
 * `cooldownEnd` vs now (`pending` before, `claimable` after). No
 * enumeration — Ethena keeps a single cooldown slot per user.
 */
export const readerEthenaCooldown: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const res = (await multicallRetry({
      chain: chainId,
      calls: [
        { address: entry.lst, name: 'cooldowns', params: [user] },
      ] as unknown as never[],
      abi: [EthenaCooldownAbi] as unknown as never,
    })) as unknown[]

    // Tuple return — accept array or struct shape from the decoder.
    const cell = res[0] as
      | [unknown, unknown]
      | { cooldownEnd?: unknown; underlyingAmount?: unknown }
      | undefined
    let cooldownEnd: bigint | undefined
    let amount: bigint | undefined
    if (Array.isArray(cell)) {
      cooldownEnd = toBigInt(cell[0])
      amount = toBigInt(cell[1])
    } else if (cell && typeof cell === 'object') {
      cooldownEnd = toBigInt(cell.cooldownEnd)
      amount = toBigInt(cell.underlyingAmount)
    }

    if (!amount || amount === 0n) return []
    const readyAt = Number(cooldownEnd ?? 0n)

    const out: LstWithdrawalRequest[] = [
      {
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: '0',
        amountUnderlying: amount.toString(),
        status: computeStatus(readyAt),
        readyAt,
      },
    ]
    return out
  },
}
